import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";

const ROLE_COPY = {
  superuser: {
    title: "Super Users",
    invitePath: "/auth/invite-superuser",
    description:
      "Create a Super User account. If email delivery fails, the username and password are shown here so you can copy them.",
    loginHint:
      "They should log in under Super Users with this username and password.",
    empty: "No Super Users yet.",
  },
  broker: {
    title: "Brokers",
    invitePath: "/auth/invite-broker",
    description:
      "Create a Broker account. Brokers can open Listing Info, Trade Info, Agent Info, and Vendor Info, and can view Progress Tracker without editing it.",
    loginHint:
      "They should log in under Broker with this username and password.",
    empty: "No Brokers yet.",
  },
  finance: {
    title: "Finance Users",
    invitePath: "/auth/invite-finance",
    description:
      "Create a Finance account. Finance users only see Income Statement, Balance Sheet, Journal Entry, A/Payable, A/R Receivable, General Account Bank Reconciliation, and HST Report. Credentials are emailed automatically.",
    loginHint:
      "They should log in under Finance with this username and password.",
    empty: "No Finance users yet.",
  },
};

const SuperUsersPanel = ({
  pendingUserIds,
  onResolved,
  roleType = "superuser",
}) => {
  const copy = ROLE_COPY[roleType] || ROLE_COPY.superuser;
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [users, setUsers] = useState([]);
  const [createdLogin, setCreatedLogin] = useState(null);

  const loadUsers = async () => {
    try {
      const res = await axiosInstance.get("/auth/users");
      setUsers((res.data || []).filter((u) => u.role === roleType));
    } catch (_) {
      setUsers([]);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [roleType]);

  const applyInviteResult = (res) => {
    setCreatedLogin({
      username: res.data.username,
      password: res.data.password,
      emailSent: res.data.emailSent,
      emailError: res.data.emailError,
    });
    if (res.data.emailSent) {
      toast.success(res.data.message);
    } else {
      toast.warning(
        res.data.emailError ||
          "Account created, but the email was not sent. Copy the password below."
      );
    }
    loadUsers();
    if (onResolved) onResolved();
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email is required.");
      return;
    }
    try {
      setSending(true);
      const res = await axiosInstance.post(copy.invitePath, {
        email: email.trim(),
        displayName: displayName.trim(),
        username: email.trim(),
      });
      applyInviteResult(res);
      setEmail("");
      setDisplayName("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send credentials.");
    } finally {
      setSending(false);
    }
  };

  const resend = async (user) => {
    try {
      setSendingId(user._id);
      const res = await axiosInstance.post(copy.invitePath, {
        email: user.email,
        displayName: user.displayName || "",
        username: user.username,
      });
      applyInviteResult(res);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send credentials.");
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-blue-900 mb-2">{copy.title}</h2>
      <p className="text-gray-600 mb-6">{copy.description}</p>
      <form onSubmit={handleInvite} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <input
          type="text"
          placeholder="Full name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="border rounded-md px-3 py-2"
        />
        <input
          type="email"
          placeholder="Email (username)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="border rounded-md px-3 py-2"
          required
        />
        <button
          type="submit"
          disabled={sending}
          className="bg-blue-900 text-white rounded-md px-4 py-2 hover:bg-blue-800 disabled:opacity-60"
        >
          {sending ? "Saving..." : "Create / resend login"}
        </button>
      </form>
      {createdLogin && (
        <div
          className={`mb-6 p-4 rounded-md border ${
            createdLogin.emailSent
              ? "bg-green-50 border-green-200"
              : "bg-yellow-50 border-yellow-300"
          }`}
        >
          <p className="font-semibold mb-2">
            {createdLogin.emailSent
              ? "Credentials emailed. You can also copy them below."
              : createdLogin.emailError ||
                "Email could not be sent. Copy these credentials and give them to the user."}
          </p>
          <p>
            <strong>Username:</strong> {createdLogin.username}
          </p>
          <p>
            <strong>Password:</strong> {createdLogin.password}
          </p>
          <p className="text-sm text-gray-600 mt-2">{copy.loginHint}</p>
        </div>
      )}
      <table className="min-w-full">
        <thead>
          <tr className="bg-blue-900 text-white">
            <th className="text-left px-3 py-2">Name</th>
            <th className="text-left px-3 py-2">Username</th>
            <th className="text-left px-3 py-2">Email status</th>
            <th className="text-left px-3 py-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr>
              <td colSpan="4" className="px-3 py-6 text-gray-500">
                {copy.empty}
              </td>
            </tr>
          ) : (
            users.map((user) => (
              <tr key={user._id} className="border-b">
                <td className="px-3 py-2">{user.displayName || "—"}</td>
                <td className="px-3 py-2">{user.username}</td>
                <td className="px-3 py-2">
                  {pendingUserIds?.has(String(user._id))
                    ? "Password requested"
                    : user.lastEmailStatus === "sent"
                    ? `Sent ${
                        user.lastInviteSentAt
                          ? new Date(user.lastInviteSentAt).toLocaleString()
                          : ""
                      }`
                    : user.lastEmailStatus === "failed"
                      ? "Not emailed (server login failed)"
                      : "Not emailed"}
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    disabled={sendingId === user._id}
                    onClick={() => resend(user)}
                    className="px-3 py-1 text-sm bg-blue-900 text-white rounded hover:bg-blue-800 disabled:opacity-60"
                  >
                    {sendingId === user._id
                      ? "Working..."
                      : pendingUserIds?.has(String(user._id))
                        ? "Resend password email"
                        : "Show new password"}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SuperUsersPanel;
